import store from './store';

declare global {
    interface Window {
        analytics: any;
    }
}

// Initialize analytics with offline detection
export const intialize = () => {
    // Check if online before initializing
    if (navigator.onLine) {
        try {
            window.analytics.initialize({
                'Segment.io': {
                    apiKey: process.env.REACT_APP_ANALYTICS_API_KEY,
                    apiHost: process.env.REACT_APP_ANALYTICS_API_HOST,
                    platform: 'web',
                },
            });
        } catch (error) {
            console.warn('Failed to initialize analytics:', error);
            // Create a dummy analytics object to prevent errors
            setupOfflineAnalytics();
        }
    } else {
        // We're offline, set up dummy analytics functions
        setupOfflineAnalytics();
    }
};

function getOS() {
    const userAgent = navigator.userAgent;
    let os = 'Unknown OS';
    let arch = 'Unknown Architecture';

    // Check OS
    if (userAgent.indexOf('Win') !== -1) {
        os = 'Windows';
        // Check for 32 or 64-bit
        if (
            userAgent.indexOf('WOW64') !== -1 ||
            userAgent.indexOf('Win64') !== -1
        ) {
            arch = '64-bit';
        } else {
            arch = '32-bit';
        }
    } else if (userAgent.indexOf('Mac') !== -1) {
        os = 'MacOS';
        // On MacOS, 64-bit architecture is the default since macOS 10.7
        if (userAgent.indexOf('Intel') !== -1) {
            arch = '64-bit';
        } else {
            arch = '32-bit'; // Older Macs, not common today
        }
    } else if (userAgent.indexOf('Linux') !== -1) {
        os = 'Linux';
        // This might require additional checks, but Linux usually depends on the distribution
        arch = '64-bit'; // Assuming 64-bit as most distributions are 64-bit by default today
    } else if (userAgent.indexOf('Android') !== -1) {
        os = 'Android';
        // Mobile devices usually have ARM architecture, commonly 64-bit now
        arch = '64-bit';
    } else if (userAgent.indexOf('iPhone') !== -1) {
        os = 'iOS';
        // iPhones have 64-bit architecture now (since iPhone 5S)
        arch = '64-bit';
    }

    return { os, arch };
}

function getDeviceType(ua: any) {
    if (/mobile/i.test(ua)) {
        return 'Mobile';
    } else if (/tablet/i.test(ua)) {
        return 'Tablet';
    } else if (/TV|SmartTV/i.test(ua)) {
        return 'TV';
    } else {
        return 'Desktop/Laptop';
    }
}

// trackEvent to handle offline scenarios
export const trackEvent = (eventName: string, data?: any) => {
    try {
        const user = store.getState().auth.user;
        const school_id = store.getState().SchoolInfo.data.school.id;
        const osInfo = getOS();
        const product = store.getState()?.product;
        const teaching_platform = store.getState()?.SchoolInfo?.data?.product;

        //optional
        const book_id = store.getState().currentBook.bookId;
        const class_id = store.getState().currentBook.classId;
        const subject_id = store.getState().currentBook.subjectId;
        const isbn = store.getState().currentBook.isbn;

        // Prepare event data
        const eventData = {
            ...data,
            timestamp: new Date().toString(),
            device: getDeviceType(navigator.userAgent),
            operating_system: osInfo.os,
            os_architecture: osInfo.arch,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            user_id: user?.id,
            user: user?.rolename,
            product_type: store.getState().product,
            environment: process.env.NODE_ENV,
            user_mobile: user?.mobile,
            school_id: school_id,
            product: product, //ActiveTeach or Buildr
            teaching_platform: teaching_platform, // ActiveTeach/Buildr/Techbook/Academia
            //optional
            book_id: book_id,
            class_id: class_id,
            subject_id: subject_id,
            isbn: isbn,
            mode:
                process.env.REACT_APP_INSTALLATION_MODE !== 'webapp'
                    ? 'offline'
                    : 'online',
        };

        // If offline, store event in local storage for later sending
        if (!navigator.onLine) {
            const offlineEvents = JSON.parse(
                localStorage.getItem('offlineAnalyticsEvents') || '[]'
            );
            offlineEvents.push({ eventName, eventData });
            localStorage.setItem(
                'offlineAnalyticsEvents',
                JSON.stringify(offlineEvents)
            );
            return;
        }

        // If online, send the event
        window.analytics.track(eventName, eventData);
    } catch (error) {
        console.warn('Failed to track event:', error);
    }
};

// Identify a user
export const identifyUser = (uniqueId: string, data?: any) => {
    window.analytics.identify(uniqueId, data);
};

export const pageView = () => {
    window.analytics.page();
};

// Function to track API requests and responses
export const trackApiRequest = (
    url: any,
    method: any,
    requestData: any,
    responseData: any
) => {
    // Track the API request event
    window.analytics.track('API Request', {
        url,
        method,
        requestData,
    });

    // Track the API response event
    window.analytics.track('API Response', {
        url,
        method,
        responseData,
    });
};

// Add these functions to your segment.js file

// Set up offline analytics placeholder functions
export function setupOfflineAnalytics() {
    if (!window.analytics) {
        window.analytics = {
            initialize: () => {},
            track: (eventName: string, data?: any) => {
                // Store events for later sending
                const offlineEvents = JSON.parse(
                    localStorage.getItem('offlineAnalyticsEvents') || '[]'
                );
                offlineEvents.push({ eventName, data });
                // Keep queue size reasonable
                if (offlineEvents.length > 100) {
                    offlineEvents.shift(); // Remove oldest event if queue gets too large
                }
                localStorage.setItem(
                    'offlineAnalyticsEvents',
                    JSON.stringify(offlineEvents)
                );
            },
            identify: () => {},
            page: () => {},
            // Add any other methods you use
        };
    }
}

// Setup listener for online events
export const setupOnlineListener = () => {
    window.addEventListener('online', () => {
        try {
            // Re-initialize analytics
            intialize();

            // Process stored events
            const offlineEvents = JSON.parse(
                localStorage.getItem('offlineAnalyticsEvents') || '[]'
            );
            if (offlineEvents.length > 0) {
                console.log(
                    `Processing ${offlineEvents.length} stored analytics events`
                );
                offlineEvents.forEach((event: any) => {
                    try {
                        window.analytics.track(event.eventName, event.data);
                    } catch (e) {
                        console.warn('Failed to process offline event:', e);
                    }
                });
                // Clear processed events
                localStorage.setItem('offlineAnalyticsEvents', '[]');
            }
        } catch (error) {
            console.warn('Failed to process offline events:', error);
        }
    });
};
